import { Tooltip, VisuallyHidden } from '@mantine/core';
import type { PropsWithChildren, ReactNode } from 'react';

import type { PremiseStatusV2 } from '@dte/otw-azure-functions/src/common/interfaces/api/PremiseStatusV2';

import { addClassName } from '../../../../../../components/form/addClassName';
import styles from './PremiseSectionHeader.module.scss';

export interface SectionHeaderProps extends PropsWithChildren {
	className?: string;
	indicator?: SectionHeaderIndicator;
	tooltip?: ReactNode;
}

export interface SectionHeaderIndicator {
	icon: ReactNode;
	label: string;
	tone: 'success' | 'warning' | 'danger';
}

export function SectionHeader(props: SectionHeaderProps): ReactNode {
	const classNames = addClassName(props.className, styles['header']);
	const toneClass = props.indicator ? styles[`indicator${props.indicator.tone}`] : undefined;
	const header = (
		<h2 className={classNames}>
			<span className={styles['title']}>{props.children}</span>
			{props.indicator ? (
				<span className={[styles['indicator'], toneClass].filter(Boolean).join(' ')}>
					<VisuallyHidden>{props.indicator.label}</VisuallyHidden>
					<span className={styles['indicatorIcon']} aria-hidden={true}>
						{props.indicator.icon}
					</span>
				</span>
			) : null}
		</h2>
	);

	if (!props.tooltip) {
		return header;
	}

	return (
		<Tooltip label={props.tooltip} withArrow={true}>
			{header}
		</Tooltip>
	);
}

export interface OrderStatusHeaderProps extends SectionHeaderProps {
	status?: PremiseStatusV2;
}

export function OrderStatusHeader(props: OrderStatusHeaderProps): ReactNode {
	const classes = [];

	if (props.status?.startDate) {
		classes.push(props.status.outage ? styles['outage'] : styles['nonOutage']);

		if (!props.status.closeDate) {
			classes.push(styles['open']);
		}
	}

	return <SectionHeader className={classes.join(' ')}>{props.children}</SectionHeader>;
}
